import React, {
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Col,
	Empty,
	Flex,
	Row,
	Space,
} from 'antd';
import { useQuery } from 'react-query';
import { Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import type {
	UpdateTcTermsRequest,
	UpdateTcTermsResponse,
} from '@api/features/fixtures/updateTcTerms';
import type { FuelTypeValue } from '@api/utils/getVesselFuelGrades';
import { getReportGroups } from '@client/lib/api';
import { sendRequest } from '@client/lib/sendRequest';
import BrokersTable from '@client/screens/fixtures/tc/BrokersTable';
import CveTable from '@client/screens/fixtures/tc/CveTable';
import RevenueItemsTable from '@client/screens/fixtures/tc/RevenueItemsTable';
import TcExpensesTables from '@client/screens/fixtures/tc/TcExpensesTables';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import BunkersTable from '@client/components/BunkersTable/BunkersTable';
import Button from '@client/components/Button';
import TcTermsDetails from '@client/components/TcTermsDetails';
import showErrorNotification from '@client/utils/showErrorNotification';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';

type TcTermsUpdateAttributes = UpdateTcTermsRequest['attributes'];

const saveTcTerms = (
	id: number,
	attributes: TcTermsUpdateAttributes,
) => sendRequest<
	UpdateTcTermsRequest,
	UpdateTcTermsResponse
>(
	`/fixtures/tc-terms/${id}`,
	'patch',
	{ id, attributes },
);

const TcTermsModalContent = ({
	onClose,
	tcTerms,
	fixtureDetails: fixtureDetailsProp,
	refreshDetails,
	locked = false,
	bunkers,
	voyageDetails,
	availableFuelGrades,
	fixtureCurrency,
	onSaveChanges,
	open = true,
}: {
	onClose: () => void;
	tcTerms: TcTermsWithIncludes;
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;
	refreshDetails: () => void | Promise<void>;
	locked?: boolean;
	bunkers: GetVoyageDetailsResponse['voyageBunkers'];
	voyageDetails?: GetVoyageDetailsResponse;
	availableFuelGrades?: Array<FuelTypeValue>;
	fixtureCurrency?: Values<typeof Currencies>;
	onSaveChanges?: () => void | Promise<void>;
	open?: boolean;
}) => {
	const [localTcTerms, setLocalTcTerms] = useState(tcTerms);
	const [localFixtureDetails, setLocalFixtureDetails] = useState(fixtureDetailsProp);
	const [pendingChanges, setPendingChanges] = useState<TcTermsUpdateAttributes>({});
	const [saving, setSaving] = useState(false);

	const hasInvoices = useMemo(() => {
		if (!voyageDetails || voyageDetails.hireInvoices.length === 0) {
			return false;
		}

		return voyageDetails.hireInvoices.filter((v) => v.deletedAt == null).length > 0;
	}, [voyageDetails?.hireInvoices]);

	useEffect(() => {
		setLocalTcTerms(tcTerms);
		setLocalFixtureDetails(fixtureDetailsProp);
		setPendingChanges({});
	}, [tcTerms, fixtureDetailsProp]);

	const {
		data: reportGroups,
	} = useQuery(
		'reportGroups',
		getReportGroups,
	);

	const currency = fixtureCurrency ?? localFixtureDetails?.currency ?? Currencies.USD;

	const onUpdateTcTerms = useCallback((attributes: TcTermsUpdateAttributes) => {
		setPendingChanges((prev) => ({ ...prev, ...attributes }));
		setLocalTcTerms((prev) => ({
			...prev,
			...attributes,
		}) as TcTermsWithIncludes);

		// bankAccountId / vesselId live on the fixture for display & dependent queries
		if ('bankAccountId' in attributes || 'vesselId' in attributes) {
			setLocalFixtureDetails((prev) => ({
				...prev,
				...('bankAccountId' in attributes ? { bankAccountId: attributes.bankAccountId ?? null } : {}),
				...('vesselId' in attributes ? { vesselId: attributes.vesselId ?? null } : {}),
			}));
		}
	}, []);

	const flushPendingTcTermsChanges = useCallback(async () => {
		if (Object.keys(pendingChanges).length === 0) {
			return;
		}

		await saveTcTerms(tcTerms.id, pendingChanges);
		setPendingChanges({});
	}, [pendingChanges, tcTerms.id]);

	const onSave = useCallback(async () => {
		setSaving(true);

		try {
			await flushPendingTcTermsChanges();
			await onSaveChanges?.();
			await refreshDetails();
		} catch (e) {
			showErrorNotification('Could not update TC Terms', e as Error);
		} finally {
			setSaving(false);
		}
	}, [flushPendingTcTermsChanges, onSaveChanges, refreshDetails]);

	// Child tables (bunkers, brokers, etc.) must always refresh after their own
	// mutations — even if flushing pending TC Terms edits fails — otherwise the
	// draft row is cleared while props still hold stale server data.
	const refreshFixture = useCallback(async () => {
		try {
			await flushPendingTcTermsChanges();
		} catch (e) {
			showErrorNotification('Could not update TC Terms', e as Error);
		}

		await onSaveChanges?.();
		await refreshDetails();
	}, [flushPendingTcTermsChanges, onSaveChanges, refreshDetails]);

	const hasPendingChanges = open && Object.keys(pendingChanges).length > 0;

	const saveButton = useSaveButton({
		onSave,
		title: 'Save & Calculate',
		loading: saving,
		hasPendingChanges,
		visible: open,
	});

	if (localTcTerms == null || localFixtureDetails == null) {
		return (<Empty />);
	}

	const voyageId = localFixtureDetails.voyageId ?? voyageDetails?.id;

	return (
		<Row gutter={[16, 16]}>
			<Col span={24}>
				<Flex justify="space-between">
					TC Terms
					<Space>
						<Button data-testid="closeModal" onClick={onClose}>
							Close
						</Button>
						{saveButton}
					</Space>
				</Flex>
			</Col>
			<Col span={12}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<TcTermsDetails
							fixtureDetails={localFixtureDetails as GetFixtureDetailsResponse<TcFixtureProps>}
							tcTerms={localTcTerms}
							onUpdate={onUpdateTcTerms}
							locked={locked}
							hasInvoices={hasInvoices}
						/>
					</Col>
					{voyageId != null && (
						<Col span={24}>
							<BunkersTable
								bunkers={bunkers}
								voyageId={voyageId}
								tcTermsId={localTcTerms.id}
								refreshDetails={refreshFixture}
								currency={currency}
								availableFuelGrades={
									availableFuelGrades ?? voyageDetails?.vesselFuelGrades
								}
								size="small"
							/>
						</Col>
					)}
				</Row>
			</Col>
			<Col span={12}>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<BrokersTable
							tcTerms={localTcTerms}
							refreshFixture={refreshFixture}
							fixtureDetails={localFixtureDetails}
							locked={locked}
						/>
					</Col>
					<Col span={24}>
						<Flex vertical gap={32}>
							<CveTable
								tcTerms={localTcTerms}
								refreshFixture={refreshFixture}
								currency={currency}
								locked={locked}
								fixtureType={localFixtureDetails.type}
							/>
							<RevenueItemsTable
								tcTerms={localTcTerms}
								refreshFixture={refreshFixture}
								currency={currency}
								locked={locked}
								fixtureType={localFixtureDetails.type}
							/>
							<TcExpensesTables
								tcTerms={localTcTerms}
								refreshFixture={refreshFixture}
								currency={currency}
								locked={locked}
							/>
						</Flex>
					</Col>
					<Col span={24}>
						<EditableDetails
							onEditValuesChange={onUpdateTcTerms}
							editing={!locked}
							hideHeader
							items={[
								{
									key: 'holdingReportGroup',
									label: 'Holding Report Group',
									type: 'select',
									editable: true,
									value: localTcTerms.holdingReportGroup ?? null,
									inputProps: {
										disabled:
											reportGroups?.holdingReportGroups == null ||
											reportGroups?.holdingReportGroups?.length === 0,
										placeholder: 'Holding report group',
										showSearch: true,
									},
									options: reportGroups?.holdingReportGroups?.map((g) => ({
										value: g,
										label: g,
									})) ?? [],
									render: () => null,
								},
								{
									key: 'investorReportGroup',
									label: 'Investor Report Group',
									type: 'select',
									editable: true,
									value: localTcTerms.investorReportGroup ?? null,
									inputProps: {
										disabled:
											reportGroups?.investorReportGroups == null ||
											reportGroups?.investorReportGroups?.length === 0,
										placeholder: 'Investor report group',
										showSearch: true,
									},
									options: reportGroups?.investorReportGroups?.map((g) => ({
										value: g,
										label: g,
									})) ?? [],
									render: () => null,
								},
								{
									key: 'clientReportGroup',
									label: 'Client Report Group',
									type: 'select',
									editable: true,
									value: localTcTerms.clientReportGroup ?? null,
									inputProps: {
										disabled:
											reportGroups?.clientReportGroups == null ||
											reportGroups?.clientReportGroups?.length === 0,
										placeholder: 'Client report group',
										showSearch: true,
									},
									options: reportGroups?.clientReportGroups?.map((g) => ({
										value: g,
										label: g,
									})) ?? [],
									render: () => null,
								},
							]}
						/>
					</Col>
					<Col span={24}>
						<EditableDetails
							onEditValuesChange={onUpdateTcTerms}
							editing={!locked}
							hideHeader
							items={[
								{
									key: 'deliveryNoticesHeader',
									label: 'Delivery',
									render: () => null,
								},
								{
									key: 'deliveryNoticesApproximate',
									label: '— Notices Approx.',
									type: 'numberList',
									value: [
										...(Array.isArray(localTcTerms.deliveryNoticesApproximate) ?
											localTcTerms.deliveryNoticesApproximate : []
										)].sort((a, b) => b - a),
									editable: true,
								},
								{
									key: 'deliveryNoticesDefinite',
									label: '— Notices Definite',
									type: 'numberList',
									value: [
										...(Array.isArray(localTcTerms.deliveryNoticesDefinite) ?
											localTcTerms.deliveryNoticesDefinite : []
										)].sort((a, b) => b - a),
									editable: true,
								},
								{
									key: 'redeliveryNoticesHeader',
									label: 'Redelivery',
									render: () => null,
								},
								{
									key: 'redeliveryNoticesApproximate',
									label: '— Notices Approx.',
									type: 'numberList',
									value: [
										...(Array.isArray(localTcTerms.redeliveryNoticesApproximate) ?
											localTcTerms.redeliveryNoticesApproximate : []
										)].sort((a, b) => b - a),
									editable: true,
								},
								{
									key: 'redeliveryNoticesDefinite',
									label: '— Notices Definite',
									type: 'numberList',
									value: [
										...(Array.isArray(localTcTerms.redeliveryNoticesDefinite) ?
											localTcTerms.redeliveryNoticesDefinite : []
										)].sort((a, b) => b - a),
									editable: true,
								},
							]}
						/>
					</Col>
				</Row>
			</Col>
			<Col span={24}>
				<Flex justify="flex-end">
					<Space>
						<Button onClick={onClose}>
							Close
						</Button>
						{saveButton}
					</Space>
				</Flex>
			</Col>
		</Row>
	);
};

export default TcTermsModalContent;
